// source: template-project@7007a338a apps/backoffice/src/hooks/useDragToScroll.ts (COPY; the table's drag-to-scroll)
import { useEffect, type RefObject } from 'react';

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="slider"]',
  '[contenteditable="true"]',
  '[data-no-drag-scroll]',
].join(', ');

const DRAG_THRESHOLD_PX = 4;

/**
 * Enables click-and-drag horizontal (and vertical) scrolling on an overflow
 * container. Skips interactive controls and table body rows so list selection
 * keeps a pointer cursor and click/double-click still fire.
 *
 * Grabbing cursor appears only while an active drag is in progress — not on
 * idle hover over rows.
 *
 * Mouse only: touch and pen already scroll the container natively (and a
 * synthetic drag would fight that), so the drag arms for `pointerType === 'mouse'`.
 */
export function useDragToScroll(
  containerRef: RefObject<HTMLElement | null>,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;
    let dragging = false;
    let armed = false;

    const isInteractiveTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(INTERACTIVE_SELECTOR));
    };

    /** Data rows are for selection/actions; drag-scroll only from chrome/header. */
    const isTableBodyRow = (target: EventTarget | null): boolean => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest('tbody tr'));
    };

    const endDrag = (event: PointerEvent) => {
      if (pointerId !== null && el.hasPointerCapture(pointerId)) {
        el.releasePointerCapture(pointerId);
      }
      if (dragging) {
        el.classList.remove('cursor-grabbing');
        // Swallow the click that fires after a drag so rows/buttons don't activate.
        const suppressClick = (clickEvent: MouseEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          el.removeEventListener('click', suppressClick, true);
        };
        el.addEventListener('click', suppressClick, true);
        window.setTimeout(() => {
          el.removeEventListener('click', suppressClick, true);
        }, 0);
      }
      pointerId = null;
      dragging = false;
      armed = false;
      void event;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;
      if (isInteractiveTarget(event.target)) return;
      if (isTableBodyRow(event.target)) return;
      armed = true;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = el.scrollLeft;
      startScrollTop = el.scrollTop;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!armed || pointerId !== event.pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
          return;
        }
        dragging = true;
        el.setPointerCapture(event.pointerId);
        el.classList.add('cursor-grabbing');
      }

      event.preventDefault();
      el.scrollLeft = startScrollLeft - dx;
      el.scrollTop = startScrollTop - dy;
    };

    const onPointerUp = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      endDrag(event);
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      // Unmounted (or disabled) mid-drag: do not leave the pointer captured by an element that no longer listens.
      if (pointerId !== null && el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
      pointerId = null;
      dragging = false;
      armed = false;
      el.classList.remove('cursor-grab', 'cursor-grabbing');
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [containerRef, enabled]);
}
